import { formatCurrency } from '@eriko/shared-types';
import { Package } from 'lucide-react';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../ui/accordion';

interface NormalizedAccount {
  id: string;
  name: string;
  number: string | null;
  accountType: string;
  accountSubType: string | null;
  currentBalance: number;
  active: boolean;
}

interface Data {
  type: string;
  mode?: 'qbo' | 'xero' | 'local';
  groupBy: 'type' | 'flat';
  accounts: NormalizedAccount[];
  groupedByType?: Record<string, NormalizedAccount[]>;
}

function AccountRow({ account }: { account: NormalizedAccount }) {
  const isNegative = account.currentBalance < 0;
  const isZero = account.currentBalance === 0;
  return (
    <tr className="group border-t border-surface-100 transition-colors hover:bg-surface-50">
      <td className="px-4.5 py-1.75 font-mono text-[12px] tabular-nums text-surface-500">
        {account.number ?? '\u2014'}
      </td>
      <td className="px-3 py-1.75 text-[13px] text-surface-700">{account.name}</td>
      <td className="px-3 py-1.75 text-[13px] text-surface-500">
        {account.accountSubType ?? '\u2014'}
      </td>
      <td
        className={`px-4.5 py-1.75 text-right font-mono text-[13px] font-medium tabular-nums ${
          isNegative
            ? 'text-neg'
            : isZero
              ? 'text-surface-400'
              : 'text-surface-900'
        }`}
      >
        {formatCurrency(account.currentBalance, 2)}
      </td>
    </tr>
  );
}

function AccountTableHead() {
  return (
    <thead>
      <tr className="bg-surface-50/60 text-[11px] uppercase tracking-[0.06em] text-surface-500">
        <th className="px-4.5 py-2 text-left font-medium">Number</th>
        <th className="px-3 py-2 text-left font-medium">Name</th>
        <th className="px-3 py-2 text-left font-medium">Subtype</th>
        <th className="px-4.5 py-2 text-right font-medium">Balance</th>
      </tr>
    </thead>
  );
}

function compareAccountNumbers(a: string | null, b: string | null): number {
  return (a ?? '\uffff').localeCompare(b ?? '\uffff', undefined, {
    numeric: true,
    sensitivity: 'base',
  });
}

export function ChartOfAccountsReport({ data }: { data: Record<string, unknown> }) {
  const d = data as unknown as Data;
  const isGrouped = d.groupBy === 'type' && d.groupedByType != null;
  const isEmpty = d.accounts.length === 0;

  return (
    <div className="overflow-hidden rounded-[10px] border border-surface-200 bg-surface-0 transition-shadow hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]">
      <div className="flex items-center justify-between border-b border-surface-100 px-4.5 py-3.5">
        <div className="flex items-center gap-2.5">
          <Package className="h-4.25 w-4.25 text-surface-400" strokeWidth={1.5} />
          <span className="text-sm font-semibold text-surface-900">Chart of Accounts</span>
        </div>
        <div className="flex items-center gap-1.5">
          {d.mode === 'local' && (
            <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
              Local ledger
            </span>
          )}
          <span className="rounded-full bg-surface-50 px-2.5 py-0.75 text-xs font-medium text-surface-500">
            {d.accounts.length} accounts
          </span>
        </div>
      </div>

      {d.mode === 'local' && (
        <div className="border-b border-surface-100 px-4.5 py-2 text-[11px] leading-relaxed text-surface-500">
          Local-ledger mode — showing bank accounts and categories as a stand-in chart. Connect QBO or Xero for the full chart of accounts.
        </div>
      )}

      {isEmpty ? (
        <div className="px-4.5 py-10 text-center text-[13px] text-surface-500">
          No accounts to display.
        </div>
      ) : isGrouped ? (
        <Accordion type="multiple">
          {Object.entries(d.groupedByType!).map(([accountType, accounts]) => {
            const sorted = accounts.slice().sort((a, b) => compareAccountNumbers(a.number, b.number));
            return (
              <AccordionItem
                key={accountType}
                value={accountType}
                className="border-b border-surface-100 last:border-b-0"
              >
                <AccordionTrigger className="flex w-full items-center gap-2 px-4.5 py-2.5 text-left select-none transition-colors hover:bg-surface-50 hover:no-underline">
                  <span className="flex-1 text-[13px] font-medium uppercase tracking-[0.04em] text-surface-700">
                    {accountType}
                  </span>
                  <span className="font-mono text-[13px] font-medium tabular-nums text-surface-500 shrink-0">
                    {accounts.length}
                  </span>
                </AccordionTrigger>
                <AccordionContent className="pb-0 pt-0">
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <AccountTableHead />
                      <tbody>
                        {sorted.map((a) => (
                          <AccountRow key={a.id} account={a} />
                        ))}
                      </tbody>
                    </table>
                  </div>
                </AccordionContent>
              </AccordionItem>
            );
          })}
        </Accordion>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <AccountTableHead />
            <tbody>
              {d.accounts
                .slice()
                .sort((a, b) => compareAccountNumbers(a.number, b.number))
                .map((a) => (
                  <AccountRow key={a.id} account={a} />
                ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
